import { InputSwitch } from "primereact/inputswitch";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";

import {
  useCheckNcciBypassAvailableQuery,
  useToggleNcciBypassMutation,
} from "../../store/queries/claims";
import { IClaim } from "../../types/claim";

function mergeClaimWithBypassCalculatedData(
  apiClaim: IClaim,
  calculatedData: unknown | undefined
): IClaim {
  if (calculatedData === undefined) {
    return apiClaim;
  }
  return {
    ...apiClaim,
    precalculatedAllowable: apiClaim.precalculatedAllowable
      ? {
          ...apiClaim.precalculatedAllowable,
          calculatedData,
        }
      : {
          id: "",
          calculatedData,
          allowableAccuracyStatus: true,
          missingCodes: [],
        },
  };
}

function ncciBypassErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const { data } = error as {
      data?: { message?: string; messages?: string[] };
    };
    if (data?.message && typeof data.message === "string") return data.message;
    if (data?.messages?.[0]) return data.messages[0];
  }
  return "An error occurred while updating NCCI bypass status.";
}

interface INCCIBypassToggleProps {
  claimId: string;
  claim: IClaim;
  onClaimUpdated: (claim: IClaim) => void;
  /** Full-tab loading (same copy as order estimate recalculation). */
  onRecalculating?: (loading: boolean) => void;
}

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  margin-bottom: 1rem;
`;

const ToggleLabel = styled.label`
  font-weight: 500;
  color: #495057;
  cursor: pointer;
`;

const BypassInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const BypassDescription = styled.span`
  font-size: 0.875rem;
  color: #6c757d;
`;

const BypassBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: #e8f5e9;
  color: #2e7d32;
  margin-left: 0.5rem;
`;

const LoadingContainer = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
`;

const InlineError = styled.div`
  font-size: 0.875rem;
  color: #c62828;
  margin-top: 0.5rem;
  line-height: 1.4;
`;

export function NCCIBypassToggle({
  claimId,
  claim,
  onClaimUpdated,
  onRecalculating,
}: INCCIBypassToggleProps) {
  const toastRef = useRef<Toast>(null);
  // The toggle reflects the EFFECTIVE bypass state: FORCED_ON, or AUTO that auto-detected a paid
  // mod-59 edit (so the switch defaults green when auto-bypass applied — per product). Falls back
  // to the FORCED_ON mirror, then false. The user can flip it; the backend records FORCED_ON/OFF.
  const effectiveBypass =
    claim.ncciBypassEffective ?? claim.ncciBypassEnabled ?? false;
  const [isEnabled, setIsEnabled] = useState(effectiveBypass);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Check if NCCI bypass is available for this claim
  const { data: availabilityData, isLoading: isCheckingAvailability } =
    useCheckNcciBypassAvailableQuery({ claimId }, { skip: !claimId });

  const [toggleNcciBypass, { isLoading: isToggling }] =
    useToggleNcciBypassMutation();

  // Sync local state with claim data when it changes (e.g. the by-id fetch resolving the
  // effective state, or a navigation to a different claim).
  useEffect(() => {
    setIsEnabled(effectiveBypass);
  }, [effectiveBypass]);

  useEffect(() => {
    setInlineError(null);
  }, [claimId]);

  const isAvailable = availabilityData?.data?.ncciBypassAvailable ?? false;

  // Hide while the initial availability check is in flight — otherwise the
  // toggle flashes in for ~1s on every claim before the API confirms there's
  // no NCCI edit.
  if (isCheckingAvailability) {
    return null;
  }

  // Once bypass is enabled, the backend reports ncciBypassAvailable=false
  // (the conflict is already bypassed). Keep the toggle visible in that
  // case so the user can still disable it.
  if (!isAvailable && !isEnabled) {
    return null;
  }

  const handleToggle = async () => {
    const newValue = !isEnabled;
    setInlineError(null);
    onRecalculating?.(true);

    try {
      const result = await toggleNcciBypass({
        claimId,
        enabled: newValue,
      }).unwrap();

      const baseClaim = result.data.claim ?? claim;
      const updatedClaim = mergeClaimWithBypassCalculatedData(
        baseClaim,
        result.data.calculatedData
      );

      setIsEnabled(newValue);
      onClaimUpdated(updatedClaim);

      const deltaCents = result.data?.bypassAmountInCents ?? 0;
      const deltaAbs = Math.abs(deltaCents) / 100;
      let deltaLabel: string;
      if (deltaCents === 0) {
        deltaLabel = "no change to expected amount";
      } else if (deltaCents > 0) {
        deltaLabel = `expected amount increased by $${deltaAbs.toFixed(2)}`;
      } else {
        deltaLabel = `expected amount decreased by $${deltaAbs.toFixed(2)}`;
      }

      toastRef.current?.show({
        severity: "success",
        summary: newValue ? "NCCI Bypass Enabled" : "NCCI Bypass Disabled",
        detail: newValue
          ? `NCCI bypass has been applied (${deltaLabel}).`
          : `NCCI bypass has been removed (${deltaLabel}).`,
        life: 5000,
      });
    } catch (error: unknown) {
      const message = ncciBypassErrorMessage(error);
      setInlineError(message);
    } finally {
      onRecalculating?.(false);
    }
  };

  return (
    <>
      <Toast ref={toastRef} />
      <ToggleContainer>
        <BypassInfo>
          <div>
            <ToggleLabel htmlFor="ncci-bypass-toggle">
              Apply NCCI Bypass
            </ToggleLabel>
            {isEnabled && (
              <BypassBadge>
                <i className="pi pi-check" style={{ fontSize: "0.7rem" }} />
                NCCI bypass applied
              </BypassBadge>
            )}
            {isCheckingAvailability && (
              <LoadingContainer>
                <ProgressSpinner
                  style={{ width: "16px", height: "16px" }}
                  strokeWidth="4"
                />
              </LoadingContainer>
            )}
          </div>
          <BypassDescription>
            An NCCI edit was detected for this claim. Enable bypass to restore
            payment for bundled codes.
          </BypassDescription>
          {inlineError && <InlineError role="alert">{inlineError}</InlineError>}
        </BypassInfo>
        <InputSwitch
          id="ncci-bypass-toggle"
          checked={isEnabled}
          onChange={handleToggle}
          disabled={isCheckingAvailability || isToggling}
        />
      </ToggleContainer>
    </>
  );
}
